/**
 * Your albums, across every group, in one scrolling row.
 *
 * Home lists groups, but a group is not what anybody is looking for — an album
 * is. "Slovenia" lives three taps inside one, and which group happens to hold
 * it is an implementation detail of how the photos were shared, not something
 * you remember. This is the shortcut, and it crosses groups deliberately.
 *
 * A row rather than a grid: it is a shortcut, not an index. The albums screen
 * inside a group is the index.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useT } from "../../lib/i18n.js";
import { EmojiIcon } from "../../components/ui/EmojiIcon.js";
import { flagFor, primaryCountry } from "../../lib/countries.js";

export interface HomeAlbum {
  id: string;
  groupId: string;
  groupName: string;
  name: string;
  emoji: string | null;
  accentColor: string | null;
  locationName: string | null;
  countryCodes: string[];
  year: number | null;
  photoCount: number;
}

interface AlbumRowProps {
  albums: HomeAlbum[];
  /** Heading text, so the same row can serve "Recent" and search results. */
  label: string;
  /** Search results are already a filtered set; scroll arrows only clutter. */
  compact?: boolean;
}

export function AlbumRow({ albums, label, compact = false }: AlbumRowProps) {
  const t = useT();
  const scroller = useRef<HTMLUListElement>(null);
  const [overflow, setOverflow] = useState<"none" | "start" | "end" | "both">(
    "none",
  );

  /*
   * Which arrows to offer. A row that fits needs none, and an arrow pointing at
   * nothing is worse than no arrow — it promises more albums than exist.
   */
  const measure = useCallback(() => {
    const element = scroller.current;
    if (!element) return;

    const max = element.scrollWidth - element.clientWidth;
    // Sub-pixel layout means scrollLeft rarely reaches max exactly.
    const atStart = element.scrollLeft <= 1;
    const atEnd = element.scrollLeft >= max - 1;

    setOverflow(max <= 1 ? "none" : atStart ? "end" : atEnd ? "start" : "both");
  }, []);

  useEffect(() => {
    measure();
    const element = scroller.current;
    if (!element) return;

    // Fonts and emoji land after first paint and change the row's width.
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [measure, albums.length]);

  const scrollBy = (direction: -1 | 1) => {
    const element = scroller.current;
    if (!element) return;
    // Most of a screenful, so one tile stays visible as an anchor.
    element.scrollBy({
      left: direction * element.clientWidth * 0.8,
      behavior: "smooth",
    });
  };

  if (albums.length === 0) return null;

  const showArrows = !compact && overflow !== "none";

  return (
    <section className="fc-albumrow" aria-label={label}>
      <div className="fc-albumrow__head">
        <h2 className="fc-home__section-label">{label}</h2>

        {showArrows && (
          <div className="fc-albumrow__arrows">
            <button
              type="button"
              className="fc-albumrow__arrow"
              aria-label={t("Scroll left")}
              disabled={overflow === "end"}
              onClick={() => scrollBy(-1)}
            >
              <ChevronIcon direction="left" />
            </button>
            <button
              type="button"
              className="fc-albumrow__arrow"
              aria-label={t("Scroll right")}
              disabled={overflow === "start"}
              onClick={() => scrollBy(1)}
            >
              <ChevronIcon direction="right" />
            </button>
          </div>
        )}
      </div>

      <ul
        className="fc-albumrow__row"
        role="list"
        ref={scroller}
        onScroll={measure}
      >
        {albums.map((album) => (
          <li key={album.id} className="fc-albumrow__item">
            <Link
              className="fc-albumrow__tile"
              to="/g/$groupId"
              params={{ groupId: album.groupId }}
              search={{ folder: album.id }}
            >
              {/*
                The accent as an attribute, washed in CSS — not as an inline
                background.

                It was the raw hue at full strength, which is a colour meant
                for a 3px label stripe and reads as a paint swatch at 64px.
                Every other surface that shows an album accent (the sidebar,
                the albums screen) mixes it to 22% behind a hairline of the
                same colour, and this now does the same.
              */}
              <span
                className="fc-albumrow__icon"
                data-accent={album.accentColor ?? undefined}
                aria-hidden="true"
              >
                {/*
                  The emoji, then the country, then the first letter.

                  Never a generic folder glyph: that would make every tile in
                  the row identical and the row useless for scanning. The
                  country is worth reaching for because an album that has one
                  and no emoji is now common — picking a country is easier
                  than finding a flag in an emoji picker, which is rather the
                  point of having asked.
                */}
                {album.emoji ? (
                  <EmojiIcon emoji={album.emoji} />
                ) : primaryCountry(album.countryCodes) ? (
                  <EmojiIcon
                    emoji={flagFor(primaryCountry(album.countryCodes)!)}
                  />
                ) : (
                  firstLetter(album.name)
                )}
              </span>

              <span className="fc-albumrow__name">{album.name}</span>

              {/*
                The group is what distinguishes two albums with the same name,
                which happens constantly — every group ends up with a "Summer".
              */}
              <span className="fc-albumrow__group">{album.groupName}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Uppercase first character, counted in code points so an emoji survives. */
function firstLetter(name: string): string {
  return [...name.trim()][0]?.toUpperCase() ?? "?";
}

function ChevronIcon({ direction }: { direction: "left" | "right" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={direction === "left" ? "M15 5l-7 7 7 7" : "M9 5l7 7-7 7"} />
    </svg>
  );
}
